import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createOperacionExterna } from '../../Redux/Actions/actions';
import ClientSelector from '../../Components/ClientSelector/ClientSelector';
import style from './OperacionExternaForm.module.css';

const OperacionExternaForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const userId = useSelector(state => state.userId);

  const agenteName = user?.user
    ? `${user.user.name || ''} ${user.user.last_name || ''}`.trim()
    : '';

  const [formData, setFormData] = useState({
    titulo: '',
    agente: agenteName,
    direccion: '',
    localidad: '',
    provincia: '',
    inmobiliariaExterna: '',
    tipoPropiedad: '',
    precio: '',
    moneda: 'USD',
    descripcion: '',
    linkReferencia: '',
    isForSale: true,
    isForRent: false,
    cerrado: {
      precioCierre: '',
      currencyCierre: 'USD',
      fecha: new Date().toISOString().split('T')[0],
      soldByAgent: false,
      soldByAgency: '',
    },
  });

  const [selectedClients, setSelectedClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCerradoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      cerrado: { ...prev.cerrado, [name]: value },
    }));
  };

  const handleOperationTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      isForSale: type === 'sale',
      isForRent: type === 'rent',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        sellerId: userId ? parseInt(userId) : null,
        clientId: selectedClients.length > 0 ? selectedClients[0].id : null,
      };

      await dispatch(createOperacionExterna(payload));
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Error al crear la operación externa');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={style.container}>
      <h2 className={style.title}>Cargar venta de Punta Compradora</h2>
      <p className={style.subtitle}>
        Registrá una operación cerrada en una propiedad externa donde participaste como punta compradora
      </p>

      {error && <div className={style.error}>{error}</div>}

      <form onSubmit={handleSubmit} className={style.form}>
        {/* Sección 1: Datos de la operación */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Datos de la operación</h3>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Título descriptivo *</label>
              <input
                type="text"
                name="titulo"
                className={style.input}
                value={formData.titulo}
                onChange={handleChange}
                placeholder="Ej: Venta Depto 3 amb. Palermo"
                required
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Agente</label>
              <input
                type="text"
                name="agente"
                className={style.input}
                value={formData.agente}
                readOnly
              />
            </div>
          </div>

          <fieldset className={style.fieldset}>
            <legend className={style.legend}>Tipo de operación</legend>
            <div className={style.radioGroup}>
              <label className={style.radioLabel}>
                <input
                  type="radio"
                  name="operationType"
                  checked={formData.isForSale}
                  onChange={() => handleOperationTypeChange('sale')}
                />
                Venta
              </label>
              <label className={style.radioLabel}>
                <input
                  type="radio"
                  name="operationType"
                  checked={formData.isForRent}
                  onChange={() => handleOperationTypeChange('rent')}
                />
                Alquiler
              </label>
            </div>
          </fieldset>

          <div className={style.formGroup}>
            <label className={style.label}>Descripción</label>
            <textarea
              name="descripcion"
              className={style.textarea}
              value={formData.descripcion}
              onChange={handleChange}
              rows="3"
              placeholder="Detalles adicionales de la operación..."
            />
          </div>
        </div>

        {/* Sección 2: Datos de la propiedad externa */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Datos de la propiedad externa</h3>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Dirección *</label>
              <input
                type="text"
                name="direccion"
                className={style.input}
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Calle y número"
                required
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Tipo de propiedad</label>
              <select
                name="tipoPropiedad"
                className={style.input}
                value={formData.tipoPropiedad}
                onChange={handleChange}
              >
                <option value="">Seleccionar...</option>
                <option value="Departamento">Departamento</option>
                <option value="Casa">Casa</option>
                <option value="PH">PH</option>
                <option value="Terreno">Terreno</option>
                <option value="Local">Local</option>
                <option value="Oficina">Oficina</option>
                <option value="Galpón">Galpón</option>
                <option value="Campo">Campo</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Localidad</label>
              <input
                type="text"
                name="localidad"
                className={style.input}
                value={formData.localidad}
                onChange={handleChange}
                placeholder="Localidad"
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Provincia</label>
              <input
                type="text"
                name="provincia"
                className={style.input}
                value={formData.provincia}
                onChange={handleChange}
                placeholder="Provincia"
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Inmobiliaria externa</label>
              <input
                type="text"
                name="inmobiliariaExterna"
                className={style.input}
                value={formData.inmobiliariaExterna}
                onChange={handleChange}
                placeholder="Nombre de la inmobiliaria vendedora"
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Link de referencia</label>
              <input
                type="url"
                name="linkReferencia"
                className={style.input}
                value={formData.linkReferencia}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Precio publicado</label>
              <input
                type="text"
                name="precio"
                className={style.input}
                value={formData.precio}
                onChange={handleChange}
                placeholder="Precio"
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Moneda</label>
              <select
                name="moneda"
                className={style.input}
                value={formData.moneda}
                onChange={handleChange}
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sección 3: Datos del cierre */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Datos del cierre</h3>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Precio de cierre *</label>
              <input
                type="text"
                name="precioCierre"
                className={style.input}
                value={formData.cerrado.precioCierre}
                onChange={handleCerradoChange}
                placeholder="Monto de cierre"
                required
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Moneda de cierre</label>
              <select
                name="currencyCierre"
                className={style.input}
                value={formData.cerrado.currencyCierre}
                onChange={handleCerradoChange}
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
              </select>
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Fecha de cierre</label>
              <input
                type="date"
                name="fecha"
                className={style.input}
                value={formData.cerrado.fecha}
                onChange={handleCerradoChange}
              />
            </div>
            <div className={style.formGroup}>
              <label className={style.label}>Inmobiliaria vendedora</label>
              <input
                type="text"
                name="soldByAgency"
                className={style.input}
                value={formData.cerrado.soldByAgency}
                onChange={handleCerradoChange}
                placeholder="Nombre de la agencia vendedora"
              />
            </div>
          </div>


        </div>

        {/* Sección 4: Vínculos */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Comprador vinculado</h3>
          <ClientSelector
            selectedClients={selectedClients}
            onSelectionChange={setSelectedClients}
            sellerId={userId}
            showCategoryFilter
            allowCreate
          />
        </div>

        {/* Acciones */}
        <div className={style.actions}>
          <button
            type="button"
            className={style.cancelButton}
            onClick={() => navigate('/home')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={style.submitButton}
            disabled={submitting}
          >
            {submitting ? 'Guardando...' : 'Registrar operación'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OperacionExternaForm;
